import React, { Context, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { createVideoCheckout } from '../services/purchase.service';
import { createAnalyticsClientEventId } from '../services/analytics.service';
import { useUserState } from './user-state.provider.ts';
import router from './router';
import { openAuthModal } from '../utils/auth-modal.events';
import { buildJoinUrl } from '../utils/join';

interface PurchaseReminderContextType {
    openReminder: (videoId: string) => void;
}

interface PurchaseReminderProviderProps {
    children: JSX.Element | JSX.Element[];
}

const PurchaseReminderContext: Context<PurchaseReminderContextType> = createContext<PurchaseReminderContextType>(null!);

const PurchaseReminderProvider = ({ children }: PurchaseReminderProviderProps) => {
    const { isLoggedIn, getUserInfo } = useUserState();
    const [isOpen, setIsOpen] = useState(false);
    const [pendingVideoId, setPendingVideoId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSlowNotice, setShowSlowNotice] = useState(false);
    const submittingRef = useRef(false);

    const openReminder = useCallback((videoId: string) => {
        if (!videoId) {
            return;
        }
        setPendingVideoId(videoId);
        setIsOpen(true);
    }, []);

    const closeReminder = useCallback(() => {
        setIsOpen(false);
        setPendingVideoId(null);
        setIsSubmitting(false);
        setShowSlowNotice(false);
        submittingRef.current = false;
    }, []);

    const handleSubscribe = () => {
        closeReminder();
        router.navigate(buildJoinUrl({
            source: 'paywall',
            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`
        }));
    };

    const handleBuyNow = async () => {
        if (!pendingVideoId) {
            return;
        }

        if (submittingRef.current) {
            return;
        }

        const userInfo = getUserInfo();
        if (!isLoggedIn || !userInfo?.token) {
            localStorage.setItem('pendingVideoPurchaseId', pendingVideoId);
            localStorage.setItem('redirectAfterLogin', window.location.href);
            closeReminder();
            openAuthModal('paid_content', window.location.href);
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        setShowSlowNotice(false);
        const slowTimer = window.setTimeout(() => {
            setShowSlowNotice(true);
        }, 8000);
        try {
            const checkoutEventId = createAnalyticsClientEventId();
            const response = await createVideoCheckout(pendingVideoId, userInfo.token, checkoutEventId);
            if (response.RedirectUrl) {
                window.location.href = response.RedirectUrl;
                return;
            }
            if (response.CheckoutUrl) {
                window.location.href = response.CheckoutUrl;
                return;
            }
            if (response.Message) {
                toast.error(response.Message);
            } else {
                toast.error('Unable to start purchase.');
            }
        } catch (error) {
            toast.error('Unable to start purchase.');
        } finally {
            window.clearTimeout(slowTimer);
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    const value = useMemo(() => ({
        openReminder
    }), [openReminder]);

    return (
        <PurchaseReminderContext.Provider value={value}>
            {children}
            {isOpen ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-labelledby="purchase-reminder-title">
                    <div className="absolute inset-0 bg-black/40" onClick={closeReminder} aria-hidden="true" />
                    <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                        <div className="flex flex-col gap-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Before you buy</p>
                            <h2 className="text-xl font-semibold text-slate-900" id="purchase-reminder-title">
                                Subscribe or buy this fight
                            </h2>
                            <p className="text-sm text-slate-600">
                                A subscription unlocks this fight and the full library. If you only want this video, you can still buy it now.
                            </p>
                        </div>
                        <div className="mt-5 flex flex-col gap-3">
                            <button
                                type="button"
                                className="w-full rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
                                onClick={handleSubscribe}
                                disabled={isSubmitting}
                            >
                                Subscribe
                            </button>
                            <button
                                type="button"
                                className="w-full rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
                                onClick={handleBuyNow}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Starting checkout...' : 'Buy Now'}
                            </button>
                        </div>
                        {showSlowNotice ? (
                            <p className="mt-3 text-xs text-slate-500">
                                This is taking longer than usual. Hang tight while we connect you to checkout.
                            </p>
                        ) : null}
                        <button
                            type="button"
                            className="mt-4 text-sm text-slate-500 underline"
                            onClick={closeReminder}
                            disabled={isSubmitting}
                        >
                            Not now
                        </button>
                    </div>
                </div>
            ) : null}
        </PurchaseReminderContext.Provider>
    );
};

export const usePurchaseReminder = () => {
    return useContext(PurchaseReminderContext);
};

export default PurchaseReminderProvider;
